import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUpDownIcon, CalendarIcon } from '@heroicons/react/24/outline';

const DateColumnFilter = ({
    columnId,
    label,
    value = '',
    onChange,
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const [isPositioned, setIsPositioned] = useState(false);

    // Calculate coordinates for the portal dropdown
    const calculateCoords = useCallback(() => {
        if (!dropdownRef.current) return null;
        const rect = dropdownRef.current.getBoundingClientRect();

        if (rect.top === 0 && rect.left === 0 && rect.width === 0) {
            return null;
        }

        let left = rect.left + rect.width / 2;
        const dropdownWidth = 280; // Slightly wider for date range
        const halfWidth = dropdownWidth / 2;
        const padding = 12;

        if (left - halfWidth < padding) {
            left = halfWidth + padding;
        } else if (left + halfWidth > window.innerWidth - padding) {
            left = window.innerWidth - halfWidth - padding;
        }

        return {
            top: rect.bottom + 6,
            left: left
        };
    }, []);

    const updatePosition = useCallback(() => {
        if (isOpen) {
            const newCoords = calculateCoords();
            if (newCoords) {
                setCoords(newCoords);
                setIsPositioned(true);
            }
        }
    }, [isOpen, calculateCoords]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !event.target.closest('.portal-date-dropdown-content')) {
                setIsOpen(false);
                setIsPositioned(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            const handleEsc = (e) => {
                if (e.key === 'Escape') setIsOpen(false);
            };
            document.addEventListener('keydown', handleEsc);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('keydown', handleEsc);
            };
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            updatePosition();

            const handleClose = () => {
                setIsOpen(false);
                setIsPositioned(false);
            };

            window.addEventListener('scroll', handleClose, true);
            window.addEventListener('resize', handleClose);
            return () => {
                window.removeEventListener('scroll', handleClose, true);
                window.removeEventListener('resize', handleClose);
            };
        }
    }, [isOpen, updatePosition]);

    const handleClear = (e) => {
        e.stopPropagation();
        onChange('');
        setIsOpen(false);
    };

    const isActive = !!value;

    const dropdownContent = (
        <div
            className={`portal-date-dropdown-content fixed z-[9999] w-[280px] bg-white border border-gray-200 rounded-lg shadow-2xl overflow-hidden ${isPositioned ? 'animate-in fade-in zoom-in-95 duration-100' : ''}`}
            style={{
                top: `${coords.top}px`,
                left: `${coords.left}px`,
                transform: 'translateX(-50%)',
                visibility: isPositioned ? 'visible' : 'hidden',
                opacity: isPositioned ? 1 : 0,
                pointerEvents: isPositioned ? 'auto' : 'none'
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">{label}</span>
                {isActive && (
                    <button 
                        onClick={handleClear}
                        className="text-[10px] font-bold text-blue-600 hover:text-blue-800 uppercase tracking-tight"
                    >
                        Reset
                    </button>
                )}
            </div>

            <div className="p-4 space-y-4">
                <div>
                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Select Date</label>
                    <input
                        type="date"
                        value={value}
                        onChange={(e) => {
                            onChange(e.target.value);
                            // Keep open if user wants to change, but user said "The filter should work according to the selected date"
                            // and usually date pickers auto-close or have an Apply.
                            // For simplicity, let's keep it open or close on change?
                            // Let's keep it open so they can see what they selected.
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                
                <div className="flex items-center justify-end pt-2 border-t border-gray-100">
                    <button
                        onClick={() => setIsOpen(false)}
                        className="bg-blue-600 text-white px-4 py-1.5 rounded text-xs font-bold hover:bg-blue-700 transition-colors shadow-sm"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div ref={dropdownRef} className="relative inline-block ml-1">
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    if (!disabled) {
                        const nextState = !isOpen;
                        if (nextState) {
                            const newCoords = calculateCoords();
                            if (newCoords) {
                                setCoords(newCoords);
                                setIsPositioned(true);
                            }
                        }
                        setIsOpen(nextState);
                    }
                }}
                disabled={disabled}
                className={`
                    p-1 rounded transition-colors flex items-center gap-1
                    ${isActive ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' : 'text-gray-400 hover:bg-gray-100'}
                    ${isOpen ? 'bg-gray-200 text-gray-700' : ''}
                `}
                title={`Filter by ${label}`}
            >
                <CalendarIcon className="h-3.5 w-3.5" />
                <ChevronUpDownIcon className="h-3.5 w-3.5" />
            </button>

            {isOpen && createPortal(dropdownContent, document.body)}
        </div>
    );
};

export default DateColumnFilter;
